/**
 * Client Portal API
 */
import api from './index'
import type {
  ClientUser,
  ClientUserCreateData,
  ClientUserUpdateData,
  ClientInvitation,
  ClientInvitationCreateData,
  AcceptInvitationData,
  InvitationValidation,
  ClientProject,
  ClientActivity,
  ClientComment,
  ClientCommentCreateData,
} from '@/types'
import type { Invoice, Proposal } from '@/types'

// ============================================================================
// Admin: Client User Management
// ============================================================================

export const clientUsersApi = {
  /**
   * List all client users
   */
  async list(companyId?: string): Promise<ClientUser[]> {
    const params = companyId ? { company_id: companyId } : {}
    const { data } = await api.get('/client-portal/users', { params })
    return data.data
  },

  /**
   * Get a client user by ID
   */
  async get(userId: string): Promise<ClientUser> {
    const { data } = await api.get(`/client-portal/users/${userId}`)
    return data.data
  },

  /**
   * Create a client user
   */
  async create(userData: ClientUserCreateData): Promise<ClientUser> {
    const { data } = await api.post('/client-portal/users', userData)
    return data.data
  },

  /**
   * Update a client user
   */
  async update(userId: string, userData: ClientUserUpdateData): Promise<ClientUser> {
    const { data } = await api.patch(`/client-portal/users/${userId}`, userData)
    return data.data
  },

  /**
   * Deactivate a client user
   */
  async deactivate(userId: string): Promise<void> {
    await api.delete(`/client-portal/users/${userId}`)
  },
}

// ============================================================================
// Admin: Invitations
// ============================================================================

export const clientInvitationsApi = {
  /**
   * List pending invitations
   */
  async list(companyId?: string): Promise<ClientInvitation[]> {
    const params = companyId ? { company_id: companyId } : {}
    const { data } = await api.get('/client-portal/invitations', { params })
    return data.data
  },

  /**
   * Create an invitation
   */
  async create(invitationData: ClientInvitationCreateData): Promise<ClientInvitation> {
    const { data } = await api.post('/client-portal/invitations', invitationData)
    return data.data
  },

  /**
   * Cancel an invitation
   */
  async cancel(invitationId: string): Promise<void> {
    await api.delete(`/client-portal/invitations/${invitationId}`)
  },

  /**
   * Resend an invitation
   */
  async resend(invitationId: string): Promise<void> {
    await api.post(`/client-portal/invitations/${invitationId}/resend`)
  },
}

// ============================================================================
// Public: Invitation Acceptance
// ============================================================================

export const invitationApi = {
  /**
   * Validate an invitation token
   */
  async validate(token: string): Promise<InvitationValidation> {
    const { data } = await api.get(`/client-portal/invitations/validate/${token}`)
    return data.data
  },

  /**
   * Accept an invitation and create account
   */
  async accept(acceptData: AcceptInvitationData): Promise<ClientUser> {
    const { data } = await api.post('/client-portal/invitations/accept', acceptData)
    return data.data
  },
}

// ============================================================================
// Client Portal: Authenticated Client Endpoints
// ============================================================================

export const clientPortalApi = {
  /**
   * Get current client user profile
   */
  async getMe(): Promise<ClientUser> {
    const { data } = await api.get('/client-portal/me')
    return data.data
  },

  /**
   * Update current client user profile
   */
  async updateMe(updates: Partial<ClientUserUpdateData>): Promise<ClientUser> {
    const { data } = await api.patch('/client-portal/me', updates)
    return data.data
  },

  /**
   * Get projects
   */
  async getProjects(): Promise<ClientProject[]> {
    const { data } = await api.get('/client-portal/projects')
    return data.data
  },

  /**
   * Get a specific project
   */
  async getProject(projectId: string): Promise<ClientProject> {
    const { data } = await api.get(`/client-portal/projects/${projectId}`)
    return data.data
  },

  /**
   * Get invoices
   */
  async getInvoices(): Promise<Invoice[]> {
    const { data } = await api.get('/client-portal/invoices')
    return data.data
  },

  /**
   * Get a specific invoice
   */
  async getInvoice(invoiceId: string): Promise<Invoice> {
    const { data } = await api.get(`/client-portal/invoices/${invoiceId}`)
    return data.data
  },

  /**
   * Get proposals
   */
  async getProposals(): Promise<Proposal[]> {
    const { data } = await api.get('/client-portal/proposals')
    return data.data
  },

  /**
   * Get a specific proposal
   */
  async getProposal(proposalId: string): Promise<Proposal> {
    const { data } = await api.get(`/client-portal/proposals/${proposalId}`)
    return data.data
  },

  /**
   * Approve a proposal
   */
  async approveProposal(proposalId: string): Promise<Proposal> {
    const { data } = await api.post(`/client-portal/proposals/${proposalId}/approve`)
    return data.data
  },

  /**
   * Reject a proposal
   */
  async rejectProposal(proposalId: string, reason?: string): Promise<Proposal> {
    const { data } = await api.post(`/client-portal/proposals/${proposalId}/reject`, { reason })
    return data.data
  },

  /**
   * Get activity feed
   */
  async getActivities(): Promise<ClientActivity[]> {
    const { data } = await api.get('/client-portal/activities')
    return data.data
  },

  /**
   * Add a comment
   */
  async addComment(commentData: ClientCommentCreateData): Promise<ClientComment> {
    const { data } = await api.post('/client-portal/comments', commentData)
    return data.data
  },
}

export default {
  users: clientUsersApi,
  invitations: clientInvitationsApi,
  invitation: invitationApi,
  portal: clientPortalApi,
}
